/**
 * Marketing Status types and helpers for product badges.
 *
 * Based on breitling-storefront-next patterns (src/integrations/algolia/types.ts
 * and src/utils/labelUtils.ts) for consistent product badge handling across
 * the multi-brand platform.
 */

import { FEATURES } from '@/lib/const';

// =============================================================================
// Marketing Status Types
// =============================================================================

/**
 * Marketing status values from Algolia.
 * These are the possible values in the marketing_status array field.
 */
export enum MarketingStatus {
  LIMITED = 'Limited',
  COUNTRY_EXCLUSIVE = 'Country Exclusive',
  ONLINE_EXCLUSIVE = 'Online Exclusive',
  BOUTIQUE_AND_ONLINE_EXCLUSIVE = 'Boutique & Online Exclusive',
  BOUTIQUE_EXCLUSIVE = 'Boutique Exclusive',
  CAPSULE = 'Capsule',
}

/**
 * Badge types that can be displayed on product cards.
 * These map to translation keys in the messages files.
 */
export type BadgeType =
  | 'new'
  | 'limited'
  | 'countryExclusive'
  | 'onlineExclusive'
  | 'boutiqueAndOnlineExclusive'
  | 'boutiqueExclusive'
  | 'capsule'
  | 'limitedEdition';

/**
 * Badge configuration with styling options.
 */
export interface BadgeConfig {
  /** Translation key for the badge text */
  labelKey: BadgeType;
  /** Default fallback text (English) */
  defaultText: string;
  /** Optional variant for brand-specific styling */
  variant?: 'primary' | 'secondary' | 'accent';
}

// =============================================================================
// Constants
// =============================================================================

/**
 * Priority order for marketing status badges.
 * When a product has multiple statuses, the first matching one wins.
 * This order matches the MarketingStatus enum order from breitling-storefront-next.
 */
export const MARKETING_STATUS_PRIORITY: MarketingStatus[] = [
  MarketingStatus.LIMITED,
  MarketingStatus.COUNTRY_EXCLUSIVE,
  MarketingStatus.ONLINE_EXCLUSIVE,
  MarketingStatus.BOUTIQUE_AND_ONLINE_EXCLUSIVE,
  MarketingStatus.BOUTIQUE_EXCLUSIVE,
  MarketingStatus.CAPSULE,
];

/**
 * Mapping from MarketingStatus to BadgeConfig.
 */
export const MARKETING_STATUS_BADGES: Record<MarketingStatus, BadgeConfig> = {
  [MarketingStatus.BOUTIQUE_AND_ONLINE_EXCLUSIVE]: {
    labelKey: 'boutiqueAndOnlineExclusive',
    defaultText: 'Boutique & Online Exclusive',
    variant: 'primary',
  },
  [MarketingStatus.BOUTIQUE_EXCLUSIVE]: {
    labelKey: 'boutiqueExclusive',
    defaultText: 'Boutique Exclusive',
    variant: 'primary',
  },
  [MarketingStatus.ONLINE_EXCLUSIVE]: {
    labelKey: 'onlineExclusive',
    defaultText: 'Online Exclusive',
    variant: 'primary',
  },
  [MarketingStatus.COUNTRY_EXCLUSIVE]: {
    labelKey: 'countryExclusive',
    defaultText: 'Country Exclusive',
    variant: 'primary',
  },
  [MarketingStatus.LIMITED]: {
    labelKey: 'limited',
    defaultText: 'Limited',
    variant: 'accent',
  },
  [MarketingStatus.CAPSULE]: {
    labelKey: 'capsule',
    defaultText: 'Capsule',
    variant: 'secondary',
  },
};

/**
 * Number of months a product is considered "new".
 */
export const NEW_PRODUCT_MONTHS = 6;

// =============================================================================
// Helper Functions
// =============================================================================

/**
 * Check if a date is within the last N months (and not in the future).
 *
 * Matches the Breitling pattern in labelUtils.ts - products launched
 * within the last 6 months are considered "new", but future launch dates
 * are also considered "new" (pre-release).
 *
 * @param date - Date to check (timestamp, Date object, or ISO string)
 * @param months - Number of months to check against
 * @returns True if the date is within the last N months or in the future
 */
export function isNewerThanMonths(
  date: number | Date | string | null | undefined,
  months: number = NEW_PRODUCT_MONTHS
): boolean {
  if (!date) return false;

  // Algolia stores as Unix timestamp (seconds), convert to milliseconds
  const launchDateMs = typeof date === 'number' ? date * 1000 : new Date(date).getTime();

  if (isNaN(launchDateMs)) return false;

  const now = Date.now();

  // If launch date is in the future, consider it "new" (pre-release)
  if (launchDateMs > now) {
    return true;
  }

  // Check if within the last N months
  const cutoffDate = new Date();
  cutoffDate.setMonth(cutoffDate.getMonth() - months);
  const cutoffMs = cutoffDate.getTime();

  return launchDateMs > cutoffMs && launchDateMs < now;
}

/**
 * Get the product badge configuration based on marketing status and launch date.
 *
 * This follows the Breitling pattern:
 * 1. If the product is new (launched within 6 months) and NOT on PDP, show "New"
 * 2. Check marketing_status array in priority order
 * 3. Fall back to "New" if launched within 6 months
 * 4. Return null if no badge applies
 *
 * @param statuses - Array of marketing status strings from Algolia
 * @param launchDate - Launch date timestamp from Algolia
 * @param options - Configuration options
 * @returns Badge configuration or null if no badge applies
 */
export function getProductBadge(
  statuses: string[] | null | undefined,
  launchDate: number | Date | string | null | undefined,
  options: {
    /** Whether this is for PDP (affects "New" badge behavior) */
    isPDP?: boolean;
    /** Override the "new product" threshold in months */
    newProductMonths?: number;
    /** Whether to check for "limited_edition" boolean field */
    limitedEdition?: boolean;
  } = {}
): BadgeConfig | null {
  const { isPDP = false, newProductMonths = NEW_PRODUCT_MONTHS, limitedEdition = false } = options;

  // FEATURES.enableNewProductBadge is a temporary global kill-switch for the
  // "New" badge — both the PLP-priority and fallback paths below. All other
  // badges keep rendering.
  const isNew = FEATURES.enableNewProductBadge && isNewerThanMonths(launchDate, newProductMonths);

  // On PLP, prioritize "New" badge if applicable
  if (isNew && !isPDP) {
    return {
      labelKey: 'new',
      defaultText: 'New',
      variant: 'accent',
    };
  }

  // Check for limited_edition boolean (some systems use this instead of marketing_status)
  if (limitedEdition) {
    return {
      labelKey: 'limitedEdition',
      defaultText: 'Limited Edition',
      variant: 'accent',
    };
  }

  // Check marketing status array in priority order
  if (statuses && statuses.length > 0) {
    for (const priorityStatus of MARKETING_STATUS_PRIORITY) {
      if (statuses.includes(priorityStatus)) {
        return MARKETING_STATUS_BADGES[priorityStatus];
      }
    }
  }

  // Fall back to "New" if launched within threshold (even on PDP)
  if (isNew) {
    return {
      labelKey: 'new',
      defaultText: 'New',
      variant: 'accent',
    };
  }

  return null;
}

/**
 * Get the badge text for a product.
 *
 * Convenience function that returns just the default text.
 * For localized text, use getProductBadge() and look up the labelKey in translations.
 *
 * @param statuses - Array of marketing status strings from Algolia
 * @param launchDate - Launch date timestamp from Algolia
 * @param options - Configuration options
 * @returns Badge text or null if no badge applies
 */
export function getProductBadgeText(
  statuses: string[] | null | undefined,
  launchDate: number | Date | string | null | undefined,
  options: Parameters<typeof getProductBadge>[2] = {}
): string | null {
  const badge = getProductBadge(statuses, launchDate, options);
  return badge?.defaultText ?? null;
}

/**
 * Type guard to check if a string is a valid MarketingStatus.
 */
export function isMarketingStatus(value: string): value is MarketingStatus {
  return Object.values(MarketingStatus).includes(value as MarketingStatus);
}
